import type { Template } from '~/api/interface';
import RightArrow from '~/assets/svg/arrow-white.svg?url';
import Resource from '~/components/resource';
import VideoPlayer from '~/components/video-player/index.vue';
import { PageKey } from '~/constants/page';
import CardSegments from './cardSegments.vue';
import { TemplateInfo } from './info';
import { Similar } from './similar';

const Arrow = defineComponent({
  setup() {
    return () => <Resource src={RightArrow} class="w-[16px] h-[16px]" />;
  },
});
const Pager = defineComponent({
  props: {
    onClick: {
      type: Function as PropType<() => void>,
    },
  },
  setup(props, { slots }) {
    return () => (
      <div
        class="absolute top-[50%] w-[48px] h-[48px] rounded-full border-[1px] border-solid border-[var(--back-0-30)] bg-[var(--text-1-40)] cursor-pointer hover:bg-[var(--text-1-60)] flex-y-center justify-center"
        onClick={props.onClick}
      >
        {slots.default?.()}
      </div>
    );
  },
});

export const TemplateDetail = defineComponent({
  props: {
    list: {
      type: Object as PropType<Template[]>,
    },
    item: {
      type: Object as PropType<Template>,
    },
    onChange: {
      type: Function as PropType<([key, value]: [string, any], data: Template) => void>,
      default: () => ({}),
    },
  },
  setup(props, { expose }) {
    const { $t } = useNuxtApp();
    const route = useRoute();
    const template = useTemplateState();
    const showToggle = route.name !== PageKey.SITE_SEARCH; // 全站搜索不展示左右切换

    provideResourceLocation({
      source: template.value.source,
    });

    const activeIndex = computed(() => {
      if (!template.value.active) {
        return -1;
      }

      const index = template.value.list?.findIndex(
        (x: { id: any }) => x.id === template.value.active?.id,
      );
      return index;
    });

    const next = (type: 1 | -1) => {
      template.value.active = template.value.list[activeIndex.value + type];
    };

    const onChange = ([key, value]: [string, any]) => {
      if (template.value.active) {
        template.value.active[key] = value;
      }

      template.value?.changeCallback?.([key, value], template.value.active);
    };

    const onSimilarClick = (list: Template[], data: Template) => {
      template.value.list = list;
      template.value.active = data;
    };

    onMounted(() => {
      trackEvent(TRACK_EVENT.PLAY_TEMPLATE_VIDEO, {
        source: template.value.source,
        template_id: template.value.id,
        template_title: template.value.title,
      });
    });

    provide('hideInfo', true);

    const currentTime = ref(0);

    function onTimeUpdate(time: number) {
      currentTime.value = time;
    }

    return () => {
      return (
        <div class="">
          <div class="flex">
            <div class="w-[816px] bg-back-2 p-[20px_28px] rounded-l-[12px]">
              {template.value.active?.full_video_url && (
                <div class=" h-[428px] rounded-[10px] bg-[#2B2B2E] overflow-hidden">
                  <VideoPlayer
                    url={template.value.active?.full_video_url}
                    auto-url={template.value.active?.preview_m3u8_url}
                    class="mr-[20px]"
                    id={template.value.active?.id}
                    aigc={template.value.active?.show_ai_icon}
                    type="template"
                    startTime={currentTime.value}
                    onTimeUpdate={onTimeUpdate}
                  />
                </div>
              )}
              <CardSegments
                ref="cardSegments"
                segments={template.value.active?.camera_moment_preview || []}
                currentTime={currentTime.value}
                onUpdate:currentTime={(time: number) => {
                  currentTime.value = time;
                }}
              />
              {template.value.active && (
                <>
                  <a-divider
                    class="!mt-[24px] !mb-[16px] h-[18px] *:text-[12px] *:leading-[18px] *:text-font-40 *:font-normal"
                    style={{ borderColor: 'var(--back-3)' }}
                  >
                    {$t('xiang-si-tui-jian')}
                  </a-divider>
                  <Similar
                    active={template.value.active}
                    onSimilarClick={onSimilarClick}
                    cardHeight={154}
                    itemNumber={4}
                    hideInfo={true}
                    isBestPractice={
                      template.value.templateSource === TEMPLATE_SOURCE.BEST_PRACTICE_PAGE
                    }
                  />
                </>
              )}
              {activeIndex.value > 0 && showToggle && (
                <Pager class="-left-[60px]" onClick={() => next(-1)}>
                  <Arrow class="rotate-180" />
                </Pager>
              )}
              {activeIndex.value < template.value.list?.length - 1 && showToggle && (
                <Pager class="-right-[60px]" onClick={() => next(1)}>
                  <Arrow />
                </Pager>
              )}
            </div>
            {template.value.active && (
              <TemplateInfo
                class="p-[20px_28px]"
                data={template.value.active}
                onChange={(params: any) => onChange(params)}
                templateSource={template.value.templateSource}
              />
            )}
          </div>
        </div>
      );
    };
  },
});
